import {Fragment, useState} from 'react';
import {format} from 'date-fns';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Loader2,
  MoreHorizontal,
  Upload,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {canViewFile} from '@common/groupFtp/files';
import {NOT_BUILT, PreviewDot} from '@internal/pages/group-pipeline/components/PreviewDot';
import {FolderChip} from '@internal/pages/group-pipeline/detail/ftp/FolderChip';
import {PayrollUploadDialog} from '@internal/pages/group-pipeline/detail/ftp/PayrollUploadDialog';
import {FileViewerDialog} from '@internal/pages/group-pipeline/detail/ftp/FileViewer';
import {useGroupFile} from '@internal/pages/group-pipeline/detail/ftp/useDocumentFile';

const FILES_PAGE = 10;
const RUNS_PAGE = 10;

const shortDate = (value: Date | string) => format(new Date(value), 'MMM d, yyyy');

function FileButtons({
  files,
  path,
  name,
}: {
  files: ReturnType<typeof useGroupFile>;
  path: string;
  name: string;
}) {
  return (
    <div className='flex items-center justify-end gap-1'>
      {canViewFile(name) && (
        <Button
          variant='ghost'
          size='sm'
          disabled={files.isLoading('view', path)}
          onClick={() => void files.view(path)}
        >
          {files.isLoading('view', path) ? (
            <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
          ) : (
            <Eye className='mr-1 h-3.5 w-3.5' />
          )}
          View
        </Button>
      )}
      <Button
        variant='ghost'
        size='sm'
        disabled={files.isLoading('download', path)}
        onClick={() => void files.download(path)}
      >
        {files.isLoading('download', path) ? (
          <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
        ) : (
          <Download className='mr-1 h-3.5 w-3.5' />
        )}
        Download
      </Button>
      {canViewFile(name) ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='sm' aria-label={`More actions for ${name}`}>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onSelect={() => files.open(path)}>
              <ExternalLink className='mr-2 h-4 w-4' />
              Open in new tab
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className='w-10 shrink-0' />
      )}
    </div>
  );
}

function FolderChips({folders}: {folders: string[]}) {
  return folders.map((name, i) => (
    <Fragment key={name}>
      {i > 0 && ', '}
      <FolderChip name={name} />
    </Fragment>
  ));
}

function SectionHeader({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className='flex items-start justify-between gap-4 px-5 py-4'>
      <div className='min-w-0'>
        <h2 className='text-base font-semibold'>{title}</h2>
        <p className='mt-1 text-sm text-gray-500'>{children}</p>
      </div>
      {action}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className='flex flex-col gap-3 border-t px-5 py-4'>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className='h-5 w-full' />
      ))}
    </div>
  );
}

function ShowMore({
  shown,
  total,
  loading,
  onClick,
}: {
  shown: number;
  total: number;
  loading: boolean;
  onClick: () => void;
}) {
  if (shown >= total) return null;
  return (
    <div className='flex items-center gap-2 border-t px-5 py-3 text-sm text-gray-500'>
      <Button variant='link' size='sm' className='h-auto p-0' disabled={loading} onClick={onClick}>
        {loading && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
        Show more
      </Button>
      · {shown} of {total} shown
    </div>
  );
}

// Payroll and census files: waiting in the automation folder or moved to its archive by CBS.
export function PayrollFilesSection({
  vbaGroupId,
  canManage,
}: {
  vbaGroupId: string;
  canManage: boolean;
}) {
  const [limit, setLimit] = useState(FILES_PAGE);
  const [uploading, setUploading] = useState(false);
  const payroll = trpc.internal.groupFtp.payrollFiles.useQuery(
    {vbaGroupId, limit},
    {placeholderData: (previous) => previous},
  );
  const files = useGroupFile(vbaGroupId);
  const data = payroll.data;
  const several = (data?.folders.length ?? 0) > 1;

  return (
    <section className='overflow-hidden rounded-lg border bg-white'>
      <SectionHeader
        title='Payroll and census files'
        action={
          canManage && (
            <Button
              variant='outline'
              className='relative shrink-0 bg-white'
              disabled={!data}
              title={NOT_BUILT}
              onClick={() => setUploading(true)}
            >
              <Upload className='mr-1.5 h-4 w-4' />
              Upload payroll or census
              <PreviewDot />
            </Button>
          )
        }
      >
        Dropped in <FolderChips folders={data?.folders ?? ['CBSAutomation']} />. CBS picks up{' '}
        <span className='font-mono text-xs'>P_</span> (payroll) and{' '}
        <span className='font-mono text-xs'>C_</span> (census) files, then archives them. Results
        are under Returned by CBS.
      </SectionHeader>
      {payroll.error ? (
        <p role='alert' className='border-t px-5 py-4 text-sm text-red-700'>
          {payroll.error.message}
        </p>
      ) : !data ? (
        <ListSkeleton />
      ) : data.files.length === 0 ? (
        <p className='border-t px-5 py-4 text-sm text-gray-500'>No files yet.</p>
      ) : (
        <>
          <table className='w-full text-sm'>
            <thead className='border-y bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500'>
              <tr>
                <th className='px-5 py-2.5 font-semibold'>File</th>
                <th className='w-28 px-3 py-2.5 font-semibold'>Type</th>
                <th className='w-28 px-3 py-2.5 font-semibold'>Where</th>
                {several && <th className='px-3 py-2.5 font-semibold'>Folder</th>}
                <th className='w-32 px-3 py-2.5 font-semibold'>Dropped</th>
                <th className='px-5 py-2.5' />
              </tr>
            </thead>
            <tbody>
              {data.files.map((f) => {
                const skipped = !f.kind && !f.archived;
                return (
                  <tr
                    key={f.path}
                    className={cn('border-b last:border-0', skipped && 'bg-amber-50/60')}
                  >
                    <td className='px-5 py-3'>
                      <p className='break-all font-mono text-xs'>{f.name}</p>
                      {skipped && (
                        <p className='mt-0.5 flex items-center gap-1 text-xs text-amber-800'>
                          <AlertTriangle className='h-3 w-3 shrink-0' />
                          CBS won't pick this up: the name needs to start with P_ or C_
                        </p>
                      )}
                    </td>
                    <td className='px-3 py-3'>
                      {f.kind === 'PAYROLL' ? (
                        'Payroll'
                      ) : f.kind === 'CENSUS' ? (
                        'Census'
                      ) : skipped ? (
                        <span className='text-amber-800'>Unknown</span>
                      ) : (
                        <span className='text-gray-500'>Other</span>
                      )}
                    </td>
                    <td className='px-3 py-3'>
                      {f.archived ? (
                        <span className='text-gray-500'>Archive</span>
                      ) : (
                        <span className='font-medium text-amber-800'>Waiting</span>
                      )}
                    </td>
                    {several && (
                      <td className='px-3 py-3'>
                        <FolderChip name={f.folder} />
                      </td>
                    )}
                    <td className='px-3 py-3 text-gray-600'>{shortDate(f.modifiedAt)}</td>
                    <td className='px-5 py-3'>
                      <FileButtons files={files} path={f.path} name={f.name} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ShowMore
            shown={data.files.length}
            total={data.total}
            loading={payroll.isPlaceholderData}
            onClick={() => setLimit((n) => n + FILES_PAGE)}
          />
        </>
      )}
      {files.viewing && (
        <FileViewerDialog
          file={files.viewing}
          onOpenChange={(open) => !open && files.closeViewer()}
        />
      )}
      {uploading && data && (
        <PayrollUploadDialog folders={data.folders} onOpenChange={setUploading} />
      )}
    </section>
  );
}

// CBS's post-analyzer runs: one folder per run, opened to see its files.
export function ReturnedRunsSection({vbaGroupId}: {vbaGroupId: string}) {
  const [limit, setLimit] = useState(RUNS_PAGE);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const runs = trpc.internal.groupFtp.returnRuns.useQuery(
    {vbaGroupId, limit},
    {placeholderData: (previous) => previous},
  );
  const data = runs.data;
  const toggle = (path: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(path)) next.add(path);
      return next;
    });

  return (
    <section className='overflow-hidden rounded-lg border bg-white'>
      <SectionHeader title='Returned by CBS'>
        From <FolderChips folders={data?.folders ?? []} />. One folder per run.
      </SectionHeader>
      {runs.error ? (
        <p role='alert' className='border-t px-5 py-4 text-sm text-red-700'>
          {runs.error.message}
        </p>
      ) : !data ? (
        <ListSkeleton />
      ) : data.runs.length === 0 ? (
        <p className='border-t px-5 py-4 text-sm text-gray-500'>No runs yet.</p>
      ) : (
        <>
          <ul>
            {data.runs.map((run) => {
              const isOpen = open.has(run.path);
              return (
                <li key={run.path} className='border-t'>
                  <button
                    type='button'
                    aria-expanded={isOpen}
                    onClick={() => toggle(run.path)}
                    className={cn(
                      'flex w-full items-center gap-3 px-5 py-3 text-left text-sm hover:bg-gray-50',
                      isOpen && 'bg-gray-50',
                    )}
                  >
                    {isOpen ? (
                      <ChevronDown className='h-4 w-4 shrink-0 text-gray-500' />
                    ) : (
                      <ChevronRight className='h-4 w-4 shrink-0 text-gray-500' />
                    )}
                    <span className='w-36 shrink-0 font-medium'>
                      {run.date ? shortDate(`${run.date}T00:00:00`) : run.name}
                    </span>
                    {run.date && (
                      <span className='font-mono text-xs text-gray-500'>{run.name}</span>
                    )}
                  </button>
                  {isOpen && <RunFiles vbaGroupId={vbaGroupId} path={run.path} />}
                </li>
              );
            })}
          </ul>
          <ShowMore
            shown={data.runs.length}
            total={data.total}
            loading={runs.isPlaceholderData}
            onClick={() => setLimit((n) => n + RUNS_PAGE)}
          />
        </>
      )}
    </section>
  );
}

function RunFiles({vbaGroupId, path}: {vbaGroupId: string; path: string}) {
  const runFiles = trpc.internal.groupFtp.returnRunFiles.useQuery({vbaGroupId, path});
  const files = useGroupFile(vbaGroupId);

  if (runFiles.error) {
    return <p className='px-12 py-3 text-sm text-red-700'>{runFiles.error.message}</p>;
  }
  if (!runFiles.data) {
    return (
      <p className='flex items-center gap-2 px-12 py-3 text-sm text-gray-500'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Opening the run
      </p>
    );
  }
  if (runFiles.data.length === 0) {
    return <p className='px-12 py-3 text-sm text-gray-500'>No files in this run.</p>;
  }
  return (
    <ul>
      {files.viewing && (
        <FileViewerDialog
          file={files.viewing}
          onOpenChange={(open) => !open && files.closeViewer()}
        />
      )}
      {runFiles.data.map((f) => (
        <li key={f.path} className='flex items-center gap-2 border-t py-1.5 pl-12 pr-5 text-sm'>
          <FileText className='h-4 w-4 shrink-0 text-gray-400' />
          <span className='min-w-0 flex-1 truncate'>{f.name}</span>
          <FileButtons files={files} path={f.path} name={f.name} />
        </li>
      ))}
    </ul>
  );
}
